<template>
    <div class="mp-page" v-if="user">
        <!-- Header -->
        <div class="mp-header">
            <div class="mp-header-left">
                <div class="mp-icon-wrap"><i class="fas fa-user-circle"></i></div>
                <div>
                    <h2 class="mp-title">My Profile</h2>
                    <span class="mp-subtitle">Manage your account details</span>
                </div>
            </div>
            <button v-if="!editMode" class="mp-edit-btn" @click="enableEditMode"><i class="fas fa-pen"></i> Edit Profile</button>
            <div v-else class="mp-header-actions">
                <button class="mp-cancel-btn" :disabled="savingProfile" @click="editMode = false"><i class="fas fa-times"></i> Cancel</button>
                <button class="mp-save-btn" :disabled="savingProfile" @click="saveProfile"><i :class="savingProfile ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i> {{ savingProfile ? 'Saving...' : 'Save Changes' }}</button>
            </div>
        </div>

        <!-- Profile Card -->
        <div class="mp-profile-card">
            <!-- Avatar Section -->
            <div class="mp-avatar-section">
                <div class="mp-avatar">
                    <span class="mp-avatar-text">{{ initials }}</span>
                </div>
                <div class="mp-avatar-info">
                    <h3 class="mp-user-name">{{ user.username }}</h3>
                    <span class="mp-user-role" v-if="user.role"><i class="fas fa-shield-alt"></i> {{ user.role.name || user.role }}</span>
                </div>
            </div>

            <div class="mp-divider"></div>

            <!-- View Mode -->
            <div v-if="!editMode" class="mp-details">
                <div class="mp-detail-row">
                    <div class="mp-detail-icon mp-ic-blue"><i class="fas fa-user"></i></div>
                    <div class="mp-detail-info">
                        <span class="mp-detail-label">Username</span>
                        <span class="mp-detail-value">{{ user.username }}</span>
                    </div>
                </div>
                <div class="mp-detail-row">
                    <div class="mp-detail-icon mp-ic-purple"><i class="fas fa-envelope"></i></div>
                    <div class="mp-detail-info">
                        <span class="mp-detail-label">Email Address</span>
                        <span class="mp-detail-value">{{ user.email || '—' }}</span>
                    </div>
                </div>
                <div class="mp-detail-row">
                    <div class="mp-detail-icon mp-ic-teal"><i class="fas fa-phone-alt"></i></div>
                    <div class="mp-detail-info">
                        <span class="mp-detail-label">Phone Number</span>
                        <span class="mp-detail-value">{{ user.phone || '—' }}</span>
                    </div>
                </div>
            </div>

            <!-- Edit Mode -->
            <form v-else class="mp-form" @submit.prevent="saveProfile">
                <div class="mp-field">
                    <label class="mp-label"><i class="fas fa-user"></i> Username <span class="mp-req">*</span></label>
                    <input type="text" class="mp-input" v-model="username" placeholder="Enter username" />
                </div>
                <div class="mp-field">
                    <label class="mp-label"><i class="fas fa-envelope"></i> Email Address <span class="mp-req">*</span></label>
                    <input type="text" class="mp-input" v-model="email" placeholder="Enter email" />
                </div>
                <div class="mp-field">
                    <label class="mp-label"><i class="fas fa-phone-alt"></i> Phone Number <span class="mp-req">*</span></label>
                    <input type="number" class="mp-input" v-model="phone" placeholder="Enter phone number" />
                </div>
                <div class="mp-field">
                    <label class="mp-label"><i class="fas fa-lock"></i> Password</label>
                    <input type="password" class="mp-input" v-model="password" placeholder="Only enter if you want to change" />
                    <span class="mp-hint">Leave blank to keep current password</span>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import { http } from "@/utils/http"
import { isMobileNumber, isValidEmail } from "@/utils/common"

export default {
    data() {
        return {
            savingProfile: false,
            editMode: false,
            username: "",
            email: "",
            phone: "",
            password: "",
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        initials() {
            if (!this.user) return '?'
            const name = this.user.username || this.user.email || ''
            return name.substring(0, 2).toUpperCase()
        },
    },
    methods: {
        ...mapActions("auth", ["getMe"]),
        enableEditMode() {
            this.username = this.user.username
            this.email = this.user.email
            this.phone = this.user.phone
            this.password = ""
            this.editMode = true
        },
        async saveProfile() {
            if (!this.username || !this.email || !this.phone) {
                return this.$toast.error("Please fill up mandatory fields!")
            }
            if (!isValidEmail(this.email)) {
                return this.$toast.error("Please enter a valid email!")
            }
            if (!isMobileNumber(this.phone)) {
                return this.$toast.error("Please enter a 10 digit phone number!")
            }
            if (this.password && this.password.length < 6) {
                return this.$toast.error("Password should be at least 6 characters!")
            }
            if (this.savingProfile) return
            try {
                this.savingProfile = true
                await http.patch("/auth/me", {
                    username: this.username,
                    email: this.email,
                    phone: this.phone,
                    password: this.password,
                })
                await this.getMe()
                this.$toast.success("Profile updated!")
                this.editMode = false
            } catch (err) {
                console.log("error in saving profile", err)
                this.$toast.error("Could not update profile!")
            }
            this.savingProfile = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../assets/variables.scss";
.mp-page { max-width:640px; animation:mpFade .3s ease; }
@keyframes mpFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.mp-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.mp-header-left { display:flex; align-items:center; gap:10px; }
.mp-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.mp-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.mp-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }
.mp-header-actions { display:flex; gap:8px; }
.mp-edit-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; i { font-size:11px; } &:hover { background:$primaryDark; } }
.mp-cancel-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:1px solid $gray200; border-radius:6px; background:white; color:$gray600; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; i { font-size:10px; } &:hover { background:$gray50; border-color:$gray300; } }
.mp-save-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:none; border-radius:6px; background:#16a34a; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; i { font-size:11px; } &:hover { background:#15803d; } &:disabled { opacity:0.6; cursor:not-allowed; } }

/* Profile Card */
.mp-profile-card { background:white; border:1px solid $gray100; border-radius:10px; box-shadow:$shadowSm; overflow:hidden; }

/* Avatar */
.mp-avatar-section { display:flex; align-items:center; gap:14px; padding:20px 24px; }
.mp-avatar { width:56px; height:56px; border-radius:$radiusFull; background:linear-gradient(135deg,$primaryColor,$primaryDark); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.mp-avatar-text { font-size:20px; font-weight:700; color:white; letter-spacing:1px; }
.mp-avatar-info { display:flex; flex-direction:column; gap:3px; }
.mp-user-name { font-size:16px; font-weight:700; color:$gray800; margin:0; }
.mp-user-role { font-size:11.5px; font-weight:600; color:$primaryColor; display:inline-flex; align-items:center; gap:5px; i { font-size:10px; } }
.mp-divider { height:1px; background:$gray100; }

/* Detail Rows */
.mp-details { padding:8px 10px; }
.mp-detail-row { display:flex; align-items:center; gap:12px; padding:12px 14px; border-radius:8px; transition:background $transitionFast; &:hover { background:$gray50; } }
.mp-detail-icon { width:34px; height:34px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
.mp-ic-blue { background:#eff6ff; color:#2563eb; }
.mp-ic-purple { background:#f5f3ff; color:#7c3aed; }
.mp-ic-teal { background:#f0fdfa; color:#0d9488; }
.mp-detail-info { display:flex; flex-direction:column; gap:1px; }
.mp-detail-label { font-size:10.5px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.mp-detail-value { font-size:13.5px; color:$gray800; font-weight:600; }

/* Form */
.mp-form { padding:16px 24px 20px; display:flex; flex-direction:column; gap:14px; }
.mp-field { display:flex; flex-direction:column; gap:5px; }
.mp-label { font-size:12px; font-weight:600; color:$gray600; display:flex; align-items:center; gap:5px; i { font-size:11px; color:$gray400; } }
.mp-req { color:#dc2626; font-weight:700; }
.mp-input { width:100%; padding:9px 12px; border:1px solid $gray200; border-radius:6px; font-size:13px; font-weight:500; color:$gray800; background:white; transition:all $transitionFast; font-family:inherit; outline:none; &::placeholder { color:$gray300; } &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,0.08); } }
.mp-hint { font-size:11px; color:$gray400; font-weight:500; }

/* Responsive */
@media(max-width:768px) { .mp-header { flex-direction:column; align-items:stretch; } .mp-header-actions { justify-content:flex-end; } .mp-avatar-section { padding:16px; } .mp-form { padding:16px; } }
</style>
